import { LabelPrinterPopoverControl } from '@/components/label-printer-popover'
import { useLabelPrinter, type LabelPrinterJob } from '@/context/label-printer'
import type { LabelPrinterDevice, LabelPrinterKind } from '@/lib/label/types'
import { cn } from '@/lib/utils'

type PrinterControlProps = {
  strategy: LabelPrinterKind
  strategyLabel: string
  printers: LabelPrinterDevice[]
  selectedPrinter: LabelPrinterDevice | null
  loadingPrinters: boolean
  error: Error | null
  onRefresh: () => Promise<void>
  onSelectPrinter: (deviceId: string) => void
  onSetStrategy: (strategy: LabelPrinterKind) => void
  onTestPrint: () => Promise<void>
  jobs: LabelPrinterJob[]
}

type PrinterSetupBannerProps = {
  message: string
  className?: string
  controlProps?: PrinterControlProps
}

export function PrinterSetupBanner({ message, className, controlProps }: PrinterSetupBannerProps) {
  const fallback = useLabelPrinter()
  const controls = controlProps ?? {
    strategy: fallback.strategy,
    strategyLabel: fallback.strategyLabel,
    printers: fallback.printers,
    selectedPrinter: fallback.selectedPrinter,
    loadingPrinters: fallback.loadingPrinters,
    error: fallback.error,
    onRefresh: fallback.refreshPrinters,
    onSelectPrinter: fallback.selectPrinter,
    onSetStrategy: fallback.setStrategy,
    onTestPrint: fallback.testPrint,
    jobs: fallback.jobs,
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-md border border-dashed border-muted bg-muted/40 p-3',
        'text-sm text-muted-foreground',
        className
      )}
      data-testid="printer-setup-banner"
    >
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">Set up label printing</p>
        <p className="text-xs text-muted-foreground">{message}</p>
      </div>
      <div className="self-start">
        <LabelPrinterPopoverControl
          strategy={controls.strategy}
          strategyLabel={controls.strategyLabel}
          printers={controls.printers}
          selectedPrinter={controls.selectedPrinter}
          loadingPrinters={controls.loadingPrinters}
          error={controls.error}
          onRefresh={controls.onRefresh}
          onSelectPrinter={controls.onSelectPrinter}
          onSetStrategy={controls.onSetStrategy}
          onTestPrint={controls.onTestPrint}
          jobs={controls.jobs}
        />
      </div>
    </div>
  )
}
